import React, { useMemo } from 'react'

import * as R from 'ramda'

import {
  Flex,
  NumericalSizingLiteral,
  StatusBadge,
  StatusBadgeVariantLiteral,
  Tooltip,
} from '@driftt/dds'
import {
  type KPICardStatusIndicatorVariantLiteral,
  KPICard,
} from '@driftt/dds.reports'

import { getScoreComponents } from 'utils/kpi'

import { Score } from './index'

interface IKPICard {
  title: string
  subtitle: string
  children?: React.ReactNode
  score: Score
  metric?: number | null
  metricRange: string
  isLoading?: boolean
  isError?: boolean
  hasNoData?: boolean
  formatMetric: (metric: number) => string
  childrenFlexGap?: NumericalSizingLiteral
  previousMetric?: number | null
  comparisonTooltipContent?: string | null
}

export const ReportKPICard: React.FC<IKPICard> = ({
  title,
  subtitle,
  score,
  metric = null,
  metricRange,
  children,
  isLoading,
  isError,
  hasNoData,
  formatMetric,
  childrenFlexGap = 'half',
  previousMetric = null,
  comparisonTooltipContent = null,
}) => {
  const { statusIndicator, badgeVariant, badgeText } = useMemo(
    () => getScoreComponents(score),
    [score]
  )

  const showPreviousMetric = useMemo(() => {
    return (
      !hasNoData &&
      !R.isNil(metric) &&
      !R.isNil(previousMetric) &&
      previousMetric > 0
    )
  }, [metric, previousMetric, hasNoData])

  return (
    <KPICard
      metric={metric}
      previousMetric={showPreviousMetric ? previousMetric : null}
      hasNoData={hasNoData}
      hasError={isError}
      isLoading={isLoading}
      statusIndicator={statusIndicator as KPICardStatusIndicatorVariantLiteral}
      formatMetric={formatMetric}
    >
      <KPICard.Header title={title} description={subtitle} />
      <KPICard.Content as={Flex} css={{ flexGrow: 1 }} direction="column">
        <Flex gap="half" direction="column" justifyContent="flex-start">
          {!R.isNil(badgeVariant) && (
            <Tooltip side="right" content={metricRange}>
              <StatusBadge variant={badgeVariant as StatusBadgeVariantLiteral}>
                {badgeText}
              </StatusBadge>
            </Tooltip>
          )}
        </Flex>
        {!R.isNil(comparisonTooltipContent) &&
        showPreviousMetric &&
        !isLoading ? (
          <Tooltip content={comparisonTooltipContent}>
            <KPICard.Metric />
          </Tooltip>
        ) : (
          <KPICard.Metric />
        )}
        {!!children && (
          <Flex
            gap={childrenFlexGap}
            direction="column"
            justifyContent="flex-end"
          >
            {children}
          </Flex>
        )}
      </KPICard.Content>
    </KPICard>
  )
}
